import { useState } from 'react';
import moment, { Moment } from 'moment';

import { DateFilters } from '../../models/date-filters';

export type FilterChipDateTimeRangeProps = {
  placeHolder: string;
  initialDateFilters: DateFilters;
  onDateFiltersChanged: (newDateFilters: DateFilters) => void;
  minimumStartDate: Moment;
  timeRange: number;
};

type FilterChipDateTimeRange = {
  filterChipIsOpen: boolean;
  closeFilterChip: () => void;
  datePickerIsOpen: boolean;
  handleOnChipClicked: () => void;
  isAnyDateFilterFilled: () => boolean;
  dateFilters: DateFilters;
  handleDateFromChange: (value: Moment | null) => void;
  openDatePicker: () => void;
  closeDatePicker: () => void;
  updateDateTo: (value: Moment | null) => void;
  maxDateForTheUntilFilter: () => Moment;
  handleCancel: () => void;
  handleApply: () => void;
};

export const useFilterChipDateTimeRange = ({
  initialDateFilters,
  onDateFiltersChanged,
  timeRange,
}: Readonly<FilterChipDateTimeRangeProps>): FilterChipDateTimeRange & {
  handleOutsideAlerter: () => void;
} => {
  const [filterChipIsOpen, setFilterChipIsOpen] = useState(false);
  const [datePickerIsOpen, setDatePickerIsOpen] = useState(false);

  const [dateFilters, setDateFilters] = useState<DateFilters>(() => ({
    dateFrom: initialDateFilters.dateFrom ? moment(initialDateFilters.dateFrom) : null,
    dateTo: initialDateFilters.dateTo ? moment(initialDateFilters.dateTo) : null,
  }));

  const defaultDateFilters: DateFilters = {
    dateFrom: moment().subtract(timeRange, 'days').endOf('day'),
    dateTo: moment().endOf('day'),
  };

  const handleOnChipClicked = (): void => {
    if (!filterChipIsOpen) {
      setDateFilters({
        dateFrom: initialDateFilters.dateFrom ? moment(initialDateFilters.dateFrom) : null,
        dateTo: initialDateFilters.dateTo ? moment(initialDateFilters.dateTo) : null,
      });
    }
    setFilterChipIsOpen((open) => !open);
  };

  const handleApply = (): void => {
    onDateFiltersChanged(dateFilters);
    setFilterChipIsOpen(false);
  };

  const handleCancel = (): void => setFilterChipIsOpen(false);

  const openDatePicker = (): void => setDatePickerIsOpen(true);

  const closeDatePicker = (): void => setDatePickerIsOpen(false);

  const closeFilterChip = (): void => setFilterChipIsOpen(false);

  const updateDateTo = (value: Moment | null): void => {
    if (value === null) {
      setDateFilters({
        dateFrom: defaultDateFilters.dateFrom,
        dateTo: defaultDateFilters.dateTo,
      });
      return;
    }

    setDateFilters((prev) => ({ ...prev, dateTo: value }));
  };

  const handleDateFromChange = (value: Moment | null): void => {
    if (value === null) {
      setDateFilters({
        dateFrom: defaultDateFilters.dateFrom,
        dateTo: defaultDateFilters.dateTo,
      });
      return;
    }

    const timeRangeMoment = moment().endOf('day').subtract(timeRange, 'days');
    let newDateTo: Moment | null = null;
    if (value?.isBefore(timeRangeMoment)) {
      newDateTo = value.clone().add(timeRange, 'days').endOf('day');
    } else if (value?.isAfter(timeRangeMoment)) {
      newDateTo = moment().endOf('day');
    }
    setDateFilters({ dateFrom: value, dateTo: newDateTo });
  };

  const isAnyDateFilterFilled = (): boolean => !!dateFilters.dateFrom || !!dateFilters.dateTo;

  const maxDateForTheUntilFilter = (): Moment =>
    dateFilters.dateFrom
      ? moment.min(moment(dateFilters.dateFrom).clone().startOf('day').add(timeRange, 'days').endOf('day'), moment().endOf('day'))
      : moment().endOf('day');

  const handleOutsideAlerter = (): void => {
    if (!datePickerIsOpen) closeFilterChip();
  };

  return {
    filterChipIsOpen,
    closeFilterChip,
    datePickerIsOpen,
    handleOnChipClicked,
    isAnyDateFilterFilled,
    dateFilters,
    handleDateFromChange,
    openDatePicker,
    closeDatePicker,
    updateDateTo,
    maxDateForTheUntilFilter,
    handleCancel,
    handleApply,
    handleOutsideAlerter,
  };
};
